import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { getPageType } from '@frameio/shared/routing';
import { useSetRequestVariableParams } from '../../hooks';
import { LEXICAL_SEARCH_TYPE, NLP_SEARCH_TYPE } from '../../shared/constants';
import {
  useQuickSearchModal,
  useSearchHistoryData,
  useTrackAdvancedSearchNavigated,
  useTrackSearchExited,
} from '../../shared/contexts';

const buttonSource = {
  ai: 'AISearch',
  standard: 'ShowAllResults',
} as const;

export const useNavigateToAdvancedSearch = () => {
  const { query, filters, closeModal, isModalOpen, scopedToProjectId } =
    useQuickSearchModal();
  const { addRecentSearch } = useSearchHistoryData();
  const setRequestVariableParams = useSetRequestVariableParams();
  const pathname = usePathname();
  const isOnSearchPage = getPageType(pathname) === 'search';

  const trackAdvancedSearchNavigated = useTrackAdvancedSearchNavigated();
  const trackSearchExited = useTrackSearchExited();

  // @todo (SRCH-2455): Replace the legacy tab input, analytics lookup, and forwarded parameter with mode; retain navigation behavior.
  return useCallback(
    ({
      event,
      highlightEnd,
      highlightStart,
      query: queryOverride,
      tab,
    }: {
      event?: React.KeyboardEvent | React.MouseEvent;
      highlightEnd?: number;
      highlightStart?: number;
      query?: string;
      tab?: 'standard' | 'ai';
    }) => {
      const searchQuery = queryOverride ?? query;
      addRecentSearch({
        mode: tab === 'ai' ? NLP_SEARCH_TYPE : LEXICAL_SEARCH_TYPE,
        query: searchQuery,
      });
      trackAdvancedSearchNavigated({
        buttonSource: (tab && buttonSource[tab]) || 'ShowAllResults',
        webSource: 'Modal',
      });
      setRequestVariableParams({
        query: searchQuery,
        filters,
        projectId: scopedToProjectId,
        tab,
        highlightStart,
        highlightEnd,
      });

      // Closes the modal if it is already on search page, otherwise let search page handle the operation
      if (isOnSearchPage && isModalOpen) {
        closeModal();
        trackSearchExited({ event });
      }
    },
    [
      addRecentSearch,
      closeModal,
      filters,
      isModalOpen,
      isOnSearchPage,
      query,
      scopedToProjectId,
      setRequestVariableParams,
      trackAdvancedSearchNavigated,
      trackSearchExited,
    ]
  );
};
